import { GetStaticProps, GetStaticPaths } from 'next';

import {
	getServerPageGetEfroOostInConvoWithArticleSlugs,
	getServerPageGetEfroOostInConvoWithDetail
} from 'api/graphql-helper';
import client from 'api/apollo-client';
import {
	BodyTextFieldsFragment,
	EfroOostInConvoWithArticleFieldsFragment,
	EfroOostInConvoWithDetailSingleFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	EfroOostInformationFieldsFragment,
	Tag
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IEfroOostInConvoWithDetailPageProps extends IBasePageProps {
	pageInformation?: EfroOostInformationFieldsFragment;
	InConvoWithDetail?: EfroOostInConvoWithDetailSingleFieldsFragment;
	inConvoWithArticleBySlug?: EfroOostInConvoWithArticleFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout: any | null;
}

const EfroOostInConvoWithDetailPage = (props: IEfroOostInConvoWithDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.inConvoWithArticleBySlug || null,
		pageQuery: () => getServerPageGetEfroOostInConvoWithDetail({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.inConvoWithArticleBySlug?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'EfroOostInConvoWithArticle' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader
							module={{
								title: page.header?.title || '',
								backgroundColor: page.header?.backgroundColor,
								excerpt: page.header?.excerpt || '',
								cover: page.header?.cover,
								tags: (page.tags as Tag[]) || []
							}}
							slug={page.slug}
						/>
					</BaseModule>
					<BaseModule defaultModuleOptions={page.bodyText?.defaultModuleOptions}>
						<BodyText module={page.bodyText as BodyTextFieldsFragment} publishedAt={page.firstPublishedAt} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
					<ModuleBuilder modules={props?.InConvoWithDetail?.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetEfroOostInConvoWithArticleSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.IN_CONVO_WITH.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.IN_CONVO_WITH.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::efro-oost-in-convo-with-article.efro-oost-in-convo-with-article',
			contentTypeName: 'efro-oost-in-convo-with-article',
			neutralStyle: true,
			hideLanguageSwitcher: true,
			hideSearchbar: true,
			isSubsite: true,
			fetchPage: () => getServerPageGetEfroOostInConvoWithDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.efroOostInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.efroOostInConvoWithArticleBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let inConvoWithDetail: any = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			// We need the detail modules only when the page is visible
			const detailResult = await getServerPageGetEfroOostInConvoWithDetail({ variables }, client);
			inConvoWithDetail = detailResult?.props?.data?.efroOostInConvoWithDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				inConvoWithArticleBySlug: passwordResult.page as any,
				InConvoWithDetail: inConvoWithDetail,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in EfroOost in gesprek met detail', error);
		return { notFound: true };
	}
};

export default EfroOostInConvoWithDetailPage;
